<template>
  <section class="device-list-card">
    <v-progress-linear v-if="loading" class="card-loading" color="primary" height="2" indeterminate />
    <header class="device-list-card__head">
      <div class="device-list-card__title"><span>{{ order }}</span>设备告警统计</div>
      <span class="device-list-card__sub">{{ stationName }} 告警数量排名 Top 10（单位：条）</span>
    </header>

    <div class="device-list-card__table">
      <div class="device-list-grid">
        <div v-for="device in sortedDevices" :key="device.id" class="device-item">
          <span class="device-identity">
            <span class="device-icon"><v-icon size="16">mdi-chip</v-icon></span>
            <span class="identity-copy">
              <strong>{{ device.name }}</strong>
              <small>{{ device.code }}</small>
            </span>
          </span>
          <span class="alarm-composition">
            <span class="composition-track">
              <i class="safety" :style="deviceSegmentStyle(device.SAFETY)"
                :title="segmentTitle('影响安全', device.SAFETY, getTotal(device))" />
              <i class="income" :style="deviceSegmentStyle(device.INCOME)"
                :title="segmentTitle('影响收益', device.INCOME, getTotal(device))" />
              <i class="normal" :style="deviceSegmentStyle(device.NORMAL)"
                :title="segmentTitle('普通告警', device.NORMAL, getTotal(device))" />
            </span>
            <span class="composition-values" aria-label="三类告警次数">
              <span class="safety"><i />{{ device.SAFETY }}</span>
              <span class="income"><i />{{ device.INCOME }}</span>
              <span class="normal"><i />{{ device.NORMAL }}</span>
            </span>
          </span>
          <span class="device-total"><strong>{{ getTotal(device) }}</strong><small>次</small></span>
        </div>
      </div>

      <div v-if="!sortedDevices.length" class="device-list-card__empty">
        <v-icon size="22">mdi-alert-circle-outline</v-icon>
        <span>暂无设备告警数据</span>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  devices: { type: Array, default: () => [] },
  stationName: { type: String, default: '' },
  order: { type: String, default: '05' },
  loading: { type: Boolean, default: false },
})

const categoryKeys = ['SAFETY', 'INCOME', 'NORMAL']

function getTotal(item) {
  return categoryKeys.reduce((total, key) => total + (item[key] || 0), 0)
}

const maxDeviceTotal = computed(() => Math.max(1, ...props.devices.map(getTotal)))

const sortedDevices = computed(() => [...props.devices]
  .sort((a, b) => getTotal(b) - getTotal(a))
  .slice(0, 10))

function deviceSegmentStyle(value) {
  return { width: `${(value / maxDeviceTotal.value) * 100}%` }
}

function segmentTitle(label, value, total) {
  const percentage = total ? Math.round((value / total) * 100) : 0
  return `${label}：${value} 次（${percentage}%）`
}
</script>

<style scoped lang="scss">
.device-list-card {
  position: relative;
  margin-bottom: 14px;
  overflow: hidden;
  color: var(--text-primary);
  background: var(--card-bg);
  border: 1px solid #fff;
  border-radius: 12px;
  box-shadow: var(--shadow-card);
  backdrop-filter: blur(6px);
}

.card-loading {
  position: absolute;
  z-index: 2;
  top: 0;
  right: 0;
  left: 0;
}

.device-list-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-height: 68px;
  padding: 12px 18px;
  border-bottom: 1px solid var(--border);
}

.device-list-card__title,
.device-list-card__sub,
.identity-copy {
  min-width: 0;
}

.device-list-card__title {
  font-size: 14px;
  font-weight: 600;

  span {
    margin-right: 8px;
    color: var(--primary);
    font-size: 10px;
    font-variant-numeric: tabular-nums;
  }
}

.device-list-card__sub {
  display: block;
  max-width: 360px;
  overflow: hidden;
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 400;
  text-overflow: ellipsis;
  text-align: right;
  white-space: nowrap;
}

.device-list-card__table {
  max-height: 420px;
  overflow: auto;
}

.device-list-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 30px;
  padding: 8px 18px 14px;
}

.device-item {
  display: grid;
  grid-template-columns: minmax(160px, 0.8fr) minmax(240px, 1.6fr) 42px;
  align-items: center;
  gap: 14px;
  min-height: 64px;
  padding: 7px 2px;
  border-bottom: 1px solid color-mix(in srgb, var(--border) 78%, transparent);
  transition: background 0.2s;

  &:hover {
    background: color-mix(in srgb, var(--primary-faint) 55%, transparent);
  }
}

.device-identity {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.device-icon {
  display: inline-flex;
  flex: 0 0 30px;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  color: var(--text-secondary);
  background: var(--surface-muted);
  border-radius: 7px;
}

.identity-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    overflow: hidden;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    overflow: hidden;
    color: var(--text-muted);
    font-size: 11px;
    line-height: 15px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.alarm-composition {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 7px;
}

.composition-track {
  display: flex;
  width: 100%;
  height: 7px;
  overflow: hidden;
  background: var(--surface-muted);
  border-radius: 6px;

  i {
    display: block;
    height: 100%;
    transition: width 0.35s ease;
  }

  .safety {
    background: var(--cat-safety);
  }

  .income {
    background: var(--cat-income);
  }

  .normal {
    background: var(--cat-normal);
  }
}

.composition-values {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 11px;
  font-variant-numeric: tabular-nums;

  span {
    display: flex;
    align-items: center;
    gap: 5px;
    font-weight: 600;

    i {
      width: 6px;
      height: 6px;
      border-radius: 2px;
    }
  }

  .safety {
    color: var(--cat-safety);

    i {
      background: var(--cat-safety);
    }
  }

  .income {
    color: var(--cat-income);

    i {
      background: var(--cat-income);
    }
  }

  .normal {
    color: var(--cat-normal);

    i {
      background: var(--cat-normal);
    }
  }
}

.device-total {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 2px;
  font-variant-numeric: tabular-nums;
  text-align: right;

  strong {
    font-size: 14px;
  }

  small {
    color: var(--text-muted);
    font-size: 10px;
  }
}

.device-total {
  color: var(--text-primary);
  font-weight: 700;
}

.device-list-card__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 112px;
  color: var(--text-muted);
  font-size: 13px;
}

@media (max-width: 1050px) {
  .device-list-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .device-list-card__head {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
